import { z } from "zod";

import { apiGet } from "../lib/api";

// ─── Email send results (customer-safe view) ───────────────────────────────────
//
// One row per (engagement, target, send). The console reads the CUSTOMER-SAFE
// projection only: status + funnel timestamps + display identity. Operator-only
// fields (token, relay_base_url, sending-profile, target_email, creds) are NEVER
// part of this schema, so they can never render in the customer-facing panel.

export const EmailSendStatusSchema = z.enum(["sent", "send_failed"]);
export type EmailSendStatus = z.infer<typeof EmailSendStatusSchema>;

export const EmailSendSchema = z.object({
  target_id: z.string(),
  engagement_id: z.string(),
  campaign_id: z.string().nullable(),
  customer_id: z.string(),
  target_display_name: z.string(),
  status: EmailSendStatusSchema,
  error: z.string().nullable().optional(),
  sent_at: z.string().nullable(),
  clicked_at: z.string().nullable(),
  submitted_at: z.string().nullable(),
  opened_at: z.string().nullable(),
});
export type EmailSend = z.infer<typeof EmailSendSchema>;

export const EmailSendListResponseSchema = z.object({
  items: z.array(EmailSendSchema),
  total: z.number(),
});
export type EmailSendListResponse = z.infer<typeof EmailSendListResponseSchema>;

// ─── Funnel-stage helper (pure logic) ──────────────────────────────────────────
//
// The monotonic funnel: sent → clicked → submitted (opened is best-effort and
// not part of the primary funnel). send_failed is a terminal off-funnel state.
// "furthest reached stage wins" — submitted implies clicked implies sent.

export const FUNNEL_STAGES = ["sent", "clicked", "submitted"] as const;
export type FunnelStage = (typeof FUNNEL_STAGES)[number] | "send_failed";

export function funnelStage(send: {
  status: EmailSendStatus;
  clicked_at: string | null;
  submitted_at: string | null;
  sent_at?: string | null;
}): FunnelStage {
  if (send.status === "send_failed") return "send_failed";
  if (send.submitted_at) return "submitted";
  if (send.clicked_at) return "clicked";
  return "sent";
}

// ─── Funnel tally (pure logic) ─────────────────────────────────────────────────

export interface FunnelCounts {
  sent: number;
  clicked: number;
  submitted: number;
  send_failed: number;
}

/** Roll up a list of sends into funnel counts (sent counts non-failed rows). */
export function tallyFunnel(
  sends: Array<{
    status: EmailSendStatus;
    clicked_at: string | null;
    submitted_at: string | null;
  }>,
): FunnelCounts {
  const counts: FunnelCounts = { sent: 0, clicked: 0, submitted: 0, send_failed: 0 };
  for (const s of sends) {
    if (s.status === "send_failed") {
      counts.send_failed += 1;
      continue;
    }
    counts.sent += 1;
    if (s.clicked_at) counts.clicked += 1;
    if (s.submitted_at) counts.submitted += 1;
  }
  return counts;
}

// ─── Fetchers ────────────────────────────────────────────────────────────────────

export function fetchEngagementEmailSends(engagementId: string) {
  return apiGet(
    `/api/engagements/${engagementId}/email-sends`,
    EmailSendListResponseSchema,
  );
}

export function fetchCampaignEmailSends(campaignId: string) {
  return apiGet(
    `/api/campaigns/${campaignId}/email-sends`,
    EmailSendListResponseSchema,
  );
}
